'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, TrendingUp } from 'lucide-react';
import Image from 'next/image';

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,47%,8%)] via-[hsl(222,47%,6%)] to-[hsl(222,60%,4%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_hsl(38,95%,54%,0.06)_0%,_transparent_60%)]" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}
      />
      <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-20 md:py-28 xl:py-36">
        <div className="max-w-3xl">
          {/* Brand logo lockup */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-amber-500/20 bg-amber-500/5 flex items-center justify-center shrink-0">
              <Image
                src="/assets/images/Untitled-1774970632290.jpeg"
                alt="Driven Dog Training logo"
                width={48}
                height={48}
                className="object-contain w-full h-full"
                priority
              />
            </div>
            <div>
              <p className="text-lg font-bold text-white leading-tight">
                Driven<span className="text-[hsl(28,85%,48%)]">Market</span>
              </p>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-amber-400 text-xs font-semibold tracking-wide">UK's Working Dog Marketplace</span>
          </div>

          <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5">
            The Marketplace for{' '}
            <span className="text-amber-400">Serious</span>{' '}
            Dog People
          </h1>

          <p className="text-slate-400 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
            Buy and sell working dog equipment in one place. Crates, bite gear, e-collars, leads — sourced from trainers who know the difference.
          </p>

          <div className="flex gap-2 max-w-xl mb-5">
            <Link
              href="/listings-feed"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-150 active:scale-95"
            >
              Browse Listings
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="/create-listing"
              className="flex items-center gap-2 border border-[hsl(222,25%,18%)] hover:border-slate-500 text-slate-300 hover:text-white px-6 py-3.5 rounded-xl text-sm font-medium transition-all duration-150"
            >
              Sell Equipment
            </Link>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-slate-600 text-xs">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Browse:</span>
            </div>
            {['Crates & Kennels', 'Bite Equipment', 'E-Collars', 'Leads & Collars', 'Breeders']?.map((term) => (
              <Link
                key={`browse-${term?.replace(/\s+/g, '-')?.toLowerCase()}`}
                href={term === 'Breeders' ? '/breeders' : `/listings-feed?cat=${term?.toLowerCase()?.replace(/\s+&\s+/g, '-')?.replace(/\s+/g, '-')}`}
                className="text-xs text-slate-500 hover:text-amber-400 bg-white/5 hover:bg-amber-500/10 border border-white/8 hover:border-amber-500/20 px-2.5 py-1 rounded-full transition-all duration-150"
              >
                {term}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}